import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

// ----------------------------------------------------------------------

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Retired: still shown so an existing value stays visible, but marked as no longer offered. */
  archived?: boolean;
}

interface MultiSelectPickerProps {
  label?: string;
  placeholder?: string;
  options: MultiSelectOption[];
  /** Selected option values. */
  value: string[];
  onChange: (value: string[]) => void;
  /** Wired to `onInputChange` for server-side search; omit for a purely local list. */
  onSearchChange?: (query: string) => void;
  loading?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
}

/**
 * Multi-select over a closed set of options, rendered as removable chips.
 *
 * Extracted because the same hand-rolled `Autocomplete multiple` — the debounce, the
 * `{label, value}` mapping, the `isOptionEqualToValue` that stops React warning on every render —
 * was already duplicated across five label call sites. Multi-select custom fields would have been
 * the sixth.
 *
 * Works from option VALUES rather than option objects, so a caller holds ids (which is what gets
 * persisted) instead of having to keep whole objects in form state.
 */
export default function MultiSelectPicker({
  label,
  placeholder,
  options,
  value,
  onChange,
  onSearchChange,
  loading,
  disabled,
  required,
  error,
  helperText,
  size = 'medium',
}: MultiSelectPickerProps) {
  const byValue = new Map(options.map((option) => [option.value, option]));

  // A value whose option isn't in the list (archived and filtered out, or not yet loaded) still
  // renders as itself rather than disappearing — silently dropping it would lose the record's data
  // on the next save.
  const selected = value.map((val) => byValue.get(val) ?? { value: val, label: val });

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      size={size}
      disabled={disabled}
      loading={loading}
      options={options}
      value={selected}
      getOptionLabel={(option) => option.label}
      getOptionKey={(option) => option.value}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      onChange={(_event, next) => onChange(next.map((option) => option.value))}
      onInputChange={onSearchChange ? (_event, query) => onSearchChange(query) : undefined}
      renderTags={(tags, getTagProps) =>
        tags.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.value}
            size="small"
            variant="soft"
            label={option.archived ? `${option.label} (archived)` : option.label}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={selected.length ? undefined : placeholder}
          required={required}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
}
